import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Static case studies data
const staticCaseStudies = [
  {
    id: "1",
    title: "Digital Transformation for FinTech Leader",
    client: "Confidential",
    industry: "Financial Services",
    challenge: "Legacy systems causing operational bottlenecks and limiting growth potential.",
    solution: "Implemented microservices architecture with modern cloud infrastructure.",
    results: ["60% reduction in processing time", "99.9% system uptime", "40% cost savings"],
    image_url: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800",
    technologies: ["AWS", "Kubernetes", "Node.js", "PostgreSQL"]
  },
  {
    id: "2",
    title: "Enterprise Recruitment Platform",
    client: "Global Tech Corp",
    industry: "Technology",
    challenge: "Needed to hire 500+ engineers across multiple regions within 6 months.",
    solution: "Deployed dedicated recruitment team with specialized tech assessment process.",
    results: ["523 placements in 5 months", "95% retention rate", "30% faster hiring"],
    image_url: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800",
    technologies: []
  },
  {
    id: "3",
    title: "Healthcare Management System",
    client: "MedCare Solutions",
    industry: "Healthcare",
    challenge: "Outdated patient management system affecting care quality and efficiency.",
    solution: "Built custom HIPAA-compliant healthcare management platform.",
    results: ["50% improvement in patient processing", "Zero security incidents", "Enhanced reporting capabilities"],
    image_url: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800",
    technologies: ["React", "Python", "MongoDB", "Docker"]
  }
];

export const CaseStudies = () => {
  const [caseStudies, setCaseStudies] = useState(staticCaseStudies);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchCaseStudies = async () => {
      try {
        const response = await axios.get(`${API}/case-studies`);
        setCaseStudies(response.data);
      } catch (error) {
        // Use static data on error
        console.log('Using static case studies data');
      }
    };
    fetchCaseStudies();
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0f1a] pt-20" data-testid="case-studies-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32" data-testid="portfolio-hero">
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/3183150/pexels-photo-3183150.jpeg"
            alt="Portfolio"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0f1a] via-[#0a0f1a]/95 to-[#0a0f1a]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 text-center">
          <span className="text-[#FF6600] text-sm font-medium tracking-wider uppercase mb-4 block">
            Portfolio
          </span>
          <h1 className="heading-1 mb-6">Our Work</h1>
          <p className="body-text max-w-2xl mx-auto">
            Discover how we've helped organizations achieve their technology and talent goals.
          </p>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="section-padding" data-testid="case-studies-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="space-y-16">
            {caseStudies.map((study, index) => (
              <div 
                key={study.id}
                className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
                data-testid={`case-study-${study.id}`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="relative rounded-2xl overflow-hidden">
                    <img 
                      src={study.image_url}
                      alt={study.title}
                      className="w-full aspect-video object-cover"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="badge-orange">{study.industry}</span>
                    </div>
                  </div>
                </div>

                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <p className="text-slate-500 text-sm mb-2">{study.client}</p>
                  <h2 className="heading-3 mb-4">{study.title}</h2>
                  
                  <div className="space-y-4 mb-6">
                    <div>
                      <h4 className="text-sm font-semibold text-[#0088CC] mb-1">Challenge</h4>
                      <p className="text-slate-400 text-sm">{study.challenge}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-[#0088CC] mb-1">Solution</h4>
                      <p className="text-slate-400 text-sm">{study.solution}</p>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-white mb-3">Results</h4>
                    <div className="space-y-2">
                      {study.results.map((result, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <CheckCircle className="text-[#0088CC] flex-shrink-0 mt-0.5" size={14} />
                          <span className="text-slate-400 text-sm">{result}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {study.technologies.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {study.technologies.map((tech, i) => (
                        <span key={i} className="tech-badge text-xs">{tech}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding border-t border-[#1e293b]" data-testid="portfolio-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="heading-2 mb-6">Ready to Start Your Project?</h2>
          <p className="body-text mb-8">
            Let's discuss how we can help you achieve similar results.
          </p>
          <Link to="/contact">
            <Button 
              className="bg-[#0088CC] hover:bg-[#0099dd] text-white font-medium px-8 py-6 text-base rounded-lg"
              data-testid="portfolio-cta-btn"
            >
              Start a Conversation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CaseStudies;
